import { IServiceFactory } from './serviceFactoryInterface';

function bindAndKeyRepo<T extends Record<string | symbol, any>>(
  instance: T
): T {
  const cache = {} as T;

  const handler: ProxyHandler<T> = {
    get(target, propertyName: keyof T, reciever) {
      // if it's in the cache, return that
      if (propertyName in cache) {
        return cache[propertyName];
      }

      // get the property value as normal
      const property = Reflect.get(target, propertyName, reciever);

      // we only know what to do for functions. anything else should be returned as-is.
      if (typeof property !== 'function') return property;

      // copy the function and bind it back to the instance
      const bound = property.bind(target);

      // update the name property to include the class name and the property key
      Object.defineProperty(bound, 'name', {
        value: `${target.constructor.name}.${String(propertyName)}`,
      });

      cache[propertyName] = bound;

      return bound;
    },
  };

  return new Proxy(instance, handler);
}

export function memoizeServiceFactory(
  implementation: IServiceFactory
): IServiceFactory {
  const cache: IServiceFactory = {} as IServiceFactory;

  const handler: ProxyHandler<IServiceFactory> = {
    get(target, propertyName: keyof IServiceFactory, reciever) {
      // if it's in the cache, return that
      if (propertyName in cache) {
        return cache[propertyName];
      }

      // get the property value as normal
      const property = Reflect.get(target, propertyName, reciever);

      // we only know what to do for zero-arg functions. anything else should be returned as-is.
      if (typeof property !== 'function' || property.length !== 0)
        return property;

      // get the actual value to cache
      const value = bindAndKeyRepo(property());

      // we need to keep the same semantics, so we wrap it in a no-args function
      const toCache = (): typeof value => value;

      // @ts-expect-error it doesn't know how to narrow the type
      cache[propertyName] = toCache;

      return toCache;
    },
  };

  return new Proxy(implementation, handler);
}
